import { useSnackbar } from "notistack"
import { useMutation, useQuery, useQueryClient } from "react-query"
import { ActivationObject, UpdateActivationObject } from "../../components/providers/ActivationObjectProvider/types"
import { ProblemDetails } from "../errors"
import { getActivationObject, getInterests, postActivationObject, putActivationObject, putInterests, putFinish } from "./services"
import { ApiInterests, ApiPutInterests } from "./types"
import type { AxiosError } from "axios"

export const QUERYKEY_ACTIVATIONOBJECT = "activation-object"
export function useActivationObjectQuery(synonym?: string) {
    return useQuery<ActivationObject, ProblemDetails>(QUERYKEY_ACTIVATIONOBJECT, () => getActivationObject(synonym), {
        enabled: synonym !== undefined,
        retry: false,
    })
}

export function useActivationObjectUpdateMutation() {
    const queryClient = useQueryClient()
    return useMutation<UpdateActivationObject, ProblemDetails, UpdateActivationObject>(
        (object: UpdateActivationObject) => putActivationObject(object),
        {
            onSuccess: (data) => {
                queryClient.setQueryData(QUERYKEY_ACTIVATIONOBJECT, {
                    ...data,
                    loadedAt: new Date(),
                })
            },
        }
    )
}
export function useUpdateWithoutRerender() {
    const client = useQueryClient()
    return useMutation<UpdateActivationObject, ProblemDetails, UpdateActivationObject>(
        (object: UpdateActivationObject) => putActivationObject(object),
        {
            onSuccess: () => {
                client.invalidateQueries(QUERYKEY_ACTIVATIONOBJECT)
            },
        }
    )
}

export function useActivationObjectCreateMutation() {
    const queryClient = useQueryClient()
    const { enqueueSnackbar } = useSnackbar()

    return useMutation<ActivationObject, AxiosError<ProblemDetails>, string>((synonym: string) => postActivationObject(synonym), {
        onSuccess: () => queryClient.invalidateQueries(QUERYKEY_ACTIVATIONOBJECT),
        onError: (res) => {
            const data = res.response?.data

            enqueueSnackbar(data?.detail, {
                variant: "error",
            })
        },
    })
}

export const QUERYKEY_INTERESTS = "interests"
export function useInterestsQuery(synonym?: string) {
    return useQuery<ApiInterests, ProblemDetails>(QUERYKEY_INTERESTS, () => getInterests(synonym), {
        enabled: synonym !== undefined,
        retry: false,
    })
}

export function useFinishMutation() {
    const queryClient = useQueryClient()
    return useMutation<ActivationObject, ProblemDetails, string>((synonym: string) => putFinish(synonym), {
        onSuccess: () => queryClient.invalidateQueries(QUERYKEY_ACTIVATIONOBJECT),
    })
}

export function useInterestsUpdateMutation() {
    const queryClient = useQueryClient()
    return useMutation<ApiInterests, ProblemDetails, ApiPutInterests>((interests: ApiPutInterests) => putInterests(interests), {
        onSuccess: (data) => {
            queryClient.setQueryData(QUERYKEY_INTERESTS, data)
        },
    })
}
